/**
 * BarGraph — DGUI component for bar charts (categories vs values).
 * Single or multi-series; vertical/horizontal; grouped or stacked.
 */

import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, Typography } from '@mui/material';
import type { BarGraphDGUIProps } from '@flowrithm/shared/src/features/ai-assist/types/dgui';

const cardSx = {
  borderRadius: 2,
  borderColor: 'rgba(4, 55, 123, 0.2)',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  mt: 1,
  mb: 1,
  overflow: 'hidden',
};

const DEFAULT_COLORS = ['#04377b', '#5c8fd4', '#8fb8e8', '#b8d4f0', '#7b9acc'];

function formatValue(
  value: number,
  format?: 'number' | 'percent' | 'currency',
  currencyCode?: string
): string {
  if (format === 'percent') return `${value}%`;
  if (format === 'currency' && currencyCode) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    } catch {
      return `${currencyCode} ${value}`;
    }
  }
  return String(value);
}

/**
 * Normalize props to standard BarGraph schema (per BarGraph.md and rithm_master_prompt).
 * Expects: categories + series ([number, ...] or [{ name, values }, ...]).
 * Only normalizes snake_case (value_format, currency_code) from backend.
 */
function normalizeBarProps(props: BarGraphDGUIProps & Record<string, unknown>): {
  title?: string;
  subtitle?: string;
  categories: string[];
  seriesList: Array<{ name: string; values: number[] }>;
  orientation: 'vertical' | 'horizontal';
  layout: 'grouped' | 'stacked';
  colors: string[];
  valueFormat: 'number' | 'percent' | 'currency';
  currencyCode?: string;
} {
  const series = props.series ?? [];
  const categories = Array.isArray(props.categories) ? props.categories : [];
  const isMulti =
    Array.isArray(series) &&
    series.length > 0 &&
    typeof series[0] === 'object' &&
    series[0] !== null &&
    'values' in (series[0] as object);
  const seriesList = isMulti
    ? (series as Array<{ name: string; values: number[] }>)
    : [{ name: '', values: (series as number[]) || [] }];
  const vf = (props.valueFormat ?? props.value_format) as string;
  const valueFormat = (vf === 'percent' || vf === 'currency' ? vf : 'number') as 'number' | 'percent' | 'currency';
  return {
    ...props,
    valueFormat,
    currencyCode: (props.currencyCode ?? props.currency_code) as string | undefined,
    categories,
    seriesList,
    orientation: (props.orientation as 'vertical' | 'horizontal') ?? 'vertical',
    layout: (props.layout as 'grouped' | 'stacked') ?? 'grouped',
    colors: Array.isArray(props.colors) ? props.colors : DEFAULT_COLORS,
  };
}

export function BarGraph(initialProps: BarGraphDGUIProps & Record<string, unknown>) {
  const normalized = normalizeBarProps(initialProps);
  const {
    title,
    subtitle,
    categories: safeCategories,
    seriesList,
    orientation = 'vertical',
    layout = 'grouped',
    colors = DEFAULT_COLORS,
    valueFormat = 'number',
    currencyCode,
  } = normalized;
  const isEmpty =
    safeCategories.length === 0 ||
    seriesList.length === 0 ||
    (seriesList[0] && seriesList[0].values?.length === 0);

  const chartData = safeCategories.map((cat, i) => {
    const point: Record<string, string | number> = { category: cat };
    seriesList.forEach((s) => {
      const vals = s.values;
      const key = s.name || 'value';
      point[key] = Array.isArray(vals) && vals[i] != null ? vals[i] : 0;
    });
    return point;
  });

  const isHorizontal = orientation === 'horizontal';
  const Layout = isHorizontal ? (
    <BarChart data={chartData} layout="vertical" margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
      <XAxis type="number" tick={{ fontSize: 11 }} />
      <YAxis type="category" dataKey="category" width={80} tick={{ fontSize: 11 }} />
      <Tooltip
        formatter={(value: number) => formatValue(value, valueFormat, currencyCode)}
        labelFormatter={(label) => String(label)}
      />
      {seriesList.length > 1 && <Legend />}
      {seriesList.map((s, idx) => (
        <Bar
          key={s.name || idx}
          dataKey={s.name || 'value'}
          stackId={layout === 'stacked' ? 'stack' : undefined}
          fill={colors[idx % colors.length]}
        />
      ))}
    </BarChart>
  ) : (
    <BarChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
      <XAxis dataKey="category" tick={{ fontSize: 11 }} />
      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => formatValue(v, valueFormat, currencyCode)} />
      <Tooltip
        formatter={(value: number) => formatValue(value, valueFormat, currencyCode)}
        labelFormatter={(label) => String(label)}
      />
      {seriesList.length > 1 && <Legend />}
      {seriesList.map((s, idx) => (
        <Bar
          key={s.name || idx}
          dataKey={s.name || 'value'}
          stackId={layout === 'stacked' ? 'stack' : undefined}
          fill={colors[idx % colors.length]}
        />
      ))}
    </BarChart>
  );

  return (
    <Card variant="outlined" sx={cardSx}>
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        {title && (
          <Typography variant="subtitle1" fontWeight={600} color="#04377b" gutterBottom>
            {title}
          </Typography>
        )}
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {subtitle}
          </Typography>
        )}
        {isEmpty ? (
          <Typography variant="body2" color="text.secondary">
            No data
          </Typography>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            {Layout}
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
